import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalLink, Trash2, Trophy, Send, Briefcase } from "lucide-react";

const STATUS_OPTS = [
  { v: "saved",     l: "Saved",     color: "#6B7280" },
  { v: "applied",   l: "Applied",   color: "#0F3D2E" },
  { v: "interview", l: "Interview", color: "#D96B27" },
  { v: "won",       l: "Won",       color: "#0F3D2E" },
  { v: "lost",      l: "Lost",      color: "#8B0000" },
];

export default function ApplicationsTab() {
  const [data, setData] = useState({ applications: [], stats: {} });
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/applications").then(r => setData(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/applications/${id}`, { status }); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    try { await api.delete(`/applications/${id}`); load(); toast.success("Removed"); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <div className="py-12 text-center text-[var(--stone)]">Loading…</div>;

  if (data.applications.length === 0) return (
    <div className="card-warm p-10 text-center">
      <Briefcase size={32} className="mx-auto text-[var(--stone)] mb-3"/>
      <h3 className="font-display text-2xl font-bold">No applications yet.</h3>
      <p className="text-[var(--stone)] mt-2">Open the Live Gigs tab and click "Track" on any gig to start logging applications and win rate.</p>
    </div>
  );

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="card-warm p-5"><div className="caption">Total tracked</div>
          <div className="font-display text-3xl font-black mt-1 font-mono">{data.stats.total || 0}</div></div>
        <div className="card-warm p-5"><div className="caption">Applied</div>
          <div className="font-display text-3xl font-black mt-1 font-mono">{data.stats.applied || 0}</div></div>
        <div className="card-warm p-5"><div className="caption">Won</div>
          <div className="font-display text-3xl font-black mt-1 font-mono text-[var(--forest)]">{data.stats.won || 0}</div></div>
        <div className="card-warm p-5" style={{background:"var(--forest)", color:"var(--alabaster)"}}>
          <div className="caption" style={{color:"#D96B27"}}>Win rate</div>
          <div className="font-display text-3xl font-black mt-1 font-mono">{data.stats.win_rate || 0}%</div></div>
      </div>

      <div className="space-y-3">
        {data.applications.map(a => {
          const st = STATUS_OPTS.find(s => s.v === a.status) || STATUS_OPTS[0];
          return (
            <div key={a.id} className="card-warm p-5" data-testid={`app-${a.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="tag-pill">{a.gig?.source || "Unknown"}</span>
                {a.gig?.category && <span className="tag-pill">{a.gig.category}</span>}
                {a.hustle_name && (
                  <Link to={`/hustle/${a.hustle_id}`} className="tag-pill" style={{background:"var(--forest)", color:"var(--alabaster)", border:"none"}}>
                    → {a.hustle_name}
                  </Link>
                )}
                <span className="ml-auto text-xs font-mono text-[var(--stone)]">
                  saved {new Date(a.saved_at).toLocaleDateString()}
                </span>
              </div>
              <h3 className="font-display text-lg font-bold">{a.gig?.title}</h3>
              {a.gig?.company && <div className="text-sm text-[var(--stone)]">{a.gig.company}</div>}
              <div className="flex flex-wrap items-center justify-between mt-3 gap-3">
                <div className="flex items-center gap-2">
                  <Select value={a.status} onValueChange={(v)=>updateStatus(a.id, v)}>
                    <SelectTrigger className="w-[140px]" data-testid={`app-status-${a.id}`}>
                      <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTS.map(o => (
                        <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="w-3 h-3 rounded-full" style={{background: st.color}} title={st.l}></span>
                </div>
                <div className="flex items-center gap-3">
                  {a.gig?.url && (
                    <a href={a.gig.url} target="_blank" rel="noopener noreferrer"
                       className="text-sm font-semibold text-[var(--forest)] flex items-center gap-1">
                      Open <ExternalLink size={12}/>
                    </a>
                  )}
                  <button onClick={()=>del(a.id)} className="text-[var(--stone)] hover:text-red-500" data-testid={`app-delete-${a.id}`}>
                    <Trash2 size={16}/>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
